import { useLocation, Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const Thanks = () => {
  const location = useLocation();
  const data = location.state || {};

  const usd = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  const handleDownload = () => {
    const logoUrl = `${window.location.origin}/logo192.png`;
    const title = 'Laser Zone Arena — Booking Receipt';
    const game = data.gameName || `Game #${data.game_id || ''}`;
    const date = data.date || '';
    const time = `${data.start || ''}${data.end ? ` - ${data.end}` : ''}`;
    const visibility = 'Public';
    const players = data.players || 1;
    const ppp = usd(data.precio_por_jugador);
    const total = usd((data.precio_por_jugador || 0) * (players || 1));

    const html = `<!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>${title}</title>
        <style>
          :root { --text:#111827; --muted:#6B7280; --line:#E5E7EB; --bg:#ffffff; }
          * { box-sizing: border-box; }
          body { margin:0; background: var(--bg); color: var(--text); font: 14px/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji"; }
          .page { max-width: 760px; margin: 24px auto; padding: 24px; border: 1px solid var(--line); border-radius: 12px; }
          .header { display:flex; align-items:center; justify-content: space-between; gap:16px; border-bottom:1px solid var(--line); padding-bottom:16px; margin-bottom:16px; }
          .brand { display:flex; align-items:center; gap:12px; }
          .brand img { width: 40px; height: 40px; border-radius: 8px; }
          .brand h1 { font-size: 18px; margin:0; }
          .title { font-weight: 600; color:#111827; }
          .section { margin: 18px 0; }
          .grid { display:grid; grid-template-columns: 1fr 1fr; gap:10px 24px; }
          .row { display:flex; gap:8px; align-items:baseline; }
          .label { color: var(--muted); min-width: 110px; }
          .value { color:#111827; font-weight: 500; }
          .table { margin-top: 6px; width:100%; border-collapse: collapse; }
          .table th, .table td { padding:10px 8px; text-align:left; border-bottom:1px solid var(--line); }
          .table th { color: var(--muted); font-weight:600; }
          .total { display:flex; justify-content: space-between; margin-top: 6px; padding-top: 10px; border-top:1px solid var(--line); font-weight:700; }
          .footer { margin-top: 18px; color: var(--muted); font-size: 12px; text-align:center; }
          @media print {
            .page { border: none; margin:0; border-radius:0; }
            .footer { margin-top: 24px; }
          }
        </style>
      </head>
      <body>
        <main class="page">
          <header class="header">
            <div class="brand">
              <img src="${logoUrl}" alt="Laser Zone Arena logo" />
              <div>
                <div class="title">Laser Zone Arena</div>
                <div style="color: var(--muted); font-size:12px;">Booking Receipt</div>
              </div>
            </div>
            <div style="text-align:right; color: var(--muted); font-size:12px;">
              <div>${new Date().toLocaleString()}</div>
            </div>
          </header>

          <section class="section">
            <div class="grid">
              <div class="row"><div class="label">Game</div><div class="value">${game}</div></div>
              <div class="row"><div class="label">Visibility</div><div class="value">${visibility}</div></div>
              <div class="row"><div class="label">Date</div><div class="value">${date}</div></div>
              <div class="row"><div class="label">Time</div><div class="value">${time}</div></div>
              <div class="row"><div class="label">Full Name</div><div class="value">${data.nombre_cliente || ''}</div></div>
              <div class="row"><div class="label">Email</div><div class="value">${data.email || ''}</div></div>
              <div class="row"><div class="label">Phone</div><div class="value">${data.telefono || ''}</div></div>
              <div class="row"><div class="label">Players</div><div class="value">${players}</div></div>
            </div>
          </section>

          <section class="section">
            <table class="table">
              <thead>
                <tr>
                  <th style="width: 60%;">Description</th>
                  <th style="width: 20%;">Qty</th>
                  <th style="width: 20%; text-align:right;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Public Room — ${game} (${time})</td>
                  <td>${players}</td>
                  <td style="text-align:right;">${ppp}</td>
                </tr>
              </tbody>
            </table>
            <div class="total"><span>Total</span><span>${total}</span></div>
          </section>

          <footer class="footer">
            Save or print this receipt for your records.
          </footer>
        </main>
        <script>window.print()</script>
      </body>
    </html>`;
    const receipt = window.open('', 'receipt');
    if (receipt) {
      receipt.document.write(html);
      receipt.document.close();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-gray-100 font-inter py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-gray-800/60 rounded-2xl border border-gray-700 p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircleIcon className="h-8 w-8 text-green-500" />
          <h1 className="text-3xl font-audiowide bg-gradient-to-r from-green-300 to-blue-400 bg-clip-text text-transparent">Thanks for your booking!</h1>
        </div>
        <p className="text-gray-300 mb-6">We’ve received your request. Here’s your summary:</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <p><span className="text-gray-400">Game:</span> <span className="font-medium">{data.gameName || `Game #${data.game_id || ''}`}</span></p>
            <p><span className="text-gray-400">Date:</span> <span className="font-medium">{data.date}</span></p>
            <p><span className="text-gray-400">Time:</span> <span className="font-medium">{data.start}{data.end ? ` - ${data.end}` : ''}</span></p>
            <p><span className="text-gray-400">Visibility:</span> <span className="font-medium">{data.type === 'private' ? 'Private' : 'Public'}</span></p>
            {data.reference && (
              <p><span className="text-gray-400">Reference:</span> <span className="font-medium">{data.reference}</span></p>
            )}
          </div>
          <div className="space-y-2">
            <p><span className="text-gray-400">Full Name:</span> <span className="font-medium">{data.nombre_cliente}</span></p>
            <p><span className="text-gray-400">Email:</span> <span className="font-medium">{data.email}</span></p>
            <p><span className="text-gray-400">Phone:</span> <span className="font-medium">{data.telefono}</span></p>
            <p><span className="text-gray-400">Players:</span> <span className="font-medium">{data.players}</span></p>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-950/40 rounded-lg border border-blue-800">
          <div className="flex justify-between text-gray-200">
            <span>Price per player</span>
            <span className="text-green-400 font-mono">{usd(data.precio_por_jugador)}</span>
          </div>
          <div className="flex justify-between text-gray-100 font-semibold border-t border-blue-800 mt-2 pt-2">
            <span>Total</span>
            <span className="text-green-400 font-mono">{usd((data.precio_por_jugador || 0) * (data.players || 1))}</span>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={handleDownload}
            className="no-underline inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            Download receipt (PDF)
          </button>
          <Link
            to="/booking-history"
            className="no-underline inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white rounded-lg font-audiowide tracking-wide transition-colors"
          >
            View booking history
          </Link>
          <Link
            to="/pricing"
            className="no-underline inline-flex items-center justify-center px-6 py-3 bg-transparent border border-gray-600 hover:bg-gray-700 text-gray-100 rounded-lg font-medium transition-colors"
          >
            Back to pricing
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Thanks;
